import { useEffect, useRef, useState, type ChangeEvent } from 'react'
import type { Person } from './portariaApi'
import { listPortaria, postPortaria } from './portariaApi'
import './facialEnrollmentPilot.css'

type Device = { id: string; name: string; active: boolean }
type FaceJob = { id: string; status: string; deviceUserId?: string; errorCode?: string | null }

async function preparePhoto(file: Blob): Promise<{ preview: string; base64: string }> {
  const bitmap = await createImageBitmap(file)
  try {
    const canvas = document.createElement('canvas')
    canvas.width = 600; canvas.height = 600
    const context = canvas.getContext('2d')
    if (!context) throw new Error('canvas_unavailable')
    context.fillStyle = '#f2f4f6'; context.fillRect(0, 0, 600, 600)
    const scale = Math.min(600 / bitmap.width, 600 / bitmap.height)
    const width = bitmap.width * scale; const height = bitmap.height * scale
    context.drawImage(bitmap, (600 - width) / 2, (600 - height) / 2, width, height)
    for (const quality of [0.85, 0.72, 0.6, 0.45]) {
      const preview = canvas.toDataURL('image/jpeg', quality)
      const base64 = preview.slice(preview.indexOf(',') + 1)
      if (Math.floor(base64.length * 3 / 4) < 100_000) return { preview, base64 }
    }
    throw new Error('photo_too_large')
  } finally { bitmap.close() }
}

export function FacialEnrollmentPilot({ clientId, people, canPairAgent }: { clientId: string; people: Person[]; canPairAgent: boolean }) {
  const [personId, setPersonId] = useState('')
  const [deviceId, setDeviceId] = useState('')
  const [selectedDevices, setSelectedDevices] = useState<string[]>([])
  const [statuses, setStatuses] = useState<{ deviceId: string; deviceName: string; status: string; jobStatus: string | null }[]>([])
  const [devices, setDevices] = useState<Device[]>([])
  const [agentKey, setAgentKey] = useState('')
  const [jobId, setJobId] = useState('')
  const [photo, setPhoto] = useState<{ preview: string; base64: string } | null>(null)
  const [cameraOpen, setCameraOpen] = useState(false)
  const [busy, setBusy] = useState(false)
  const [message, setMessage] = useState('')
  const [error, setError] = useState('')
  const video = useRef<HTMLVideoElement>(null)
  const stream = useRef<MediaStream | null>(null)

  function stopCamera() {
    stream.current?.getTracks().forEach(track => track.stop())
    stream.current = null
    setCameraOpen(false)
  }
  useEffect(() => () => { stream.current?.getTracks().forEach(track => track.stop()) }, [])
  useEffect(() => {
    let active = true
    void listPortaria<Device>(clientId, 'facial-devices').then(items => { if (active) setDevices(items) })
      .catch(() => { if (active) setError('Não foi possível carregar os controladores faciais.') })
    return () => { active = false }
  }, [clientId])
  useEffect(() => {
    if (!personId) { setStatuses([]); return }
    const refresh = () => void listPortaria<{ deviceId: string; deviceName: string; status: string; jobStatus: string | null }>(clientId, `people/${personId}/face-status`).then(setStatuses).catch(() => setStatuses([]))
    refresh(); const timer = window.setInterval(refresh, 5000)
    return () => window.clearInterval(timer)
  }, [clientId, personId])
  useEffect(() => {
    if (!jobId || !personId) return
    const refresh = async () => {
      try {
        const jobs = await listPortaria<FaceJob>(clientId, `people/${personId}/face-jobs`)
        const job = jobs.find(item => item.id === jobId)
        if (job?.status === 'succeeded') { setMessage('Face cadastrada no controlador. Confirme o reconhecimento no aparelho.'); setJobId('') }
        if (job?.status === 'failed' || job?.status === 'expired') {
          setError(`O controlador não confirmou o cadastro (${job.errorCode ?? job.status}).`); setJobId('')
        }
      } catch { /* A próxima consulta tentará novamente. */ }
    }
    void refresh()
    const timer = window.setInterval(() => void refresh(), 5000)
    return () => window.clearInterval(timer)
  }, [clientId, personId, jobId])

  async function loadPhoto(file?: Blob) {
    if (!file) return
    setError(''); setMessage('')
    try { setPhoto(await preparePhoto(file)) }
    catch { setError('Não foi possível preparar a foto. Escolha outra imagem nítida do rosto.') }
  }
  async function choosePhoto(event: ChangeEvent<HTMLInputElement>) {
    await loadPhoto(event.currentTarget.files?.[0])
    event.currentTarget.value = ''
  }
  async function openCamera() {
    setError('')
    try {
      const next = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'user' }, audio: false })
      stream.current = next; setCameraOpen(true)
      if (video.current) video.current.srcObject = next
    } catch { setError('Não foi possível abrir a câmera. Confira a permissão do navegador.') }
  }
  async function capture() {
    const frame = video.current
    if (!frame || !frame.videoWidth) return
    const canvas = document.createElement('canvas')
    canvas.width = frame.videoWidth; canvas.height = frame.videoHeight
    canvas.getContext('2d')?.drawImage(frame, 0, 0)
    const blob = await new Promise<Blob | null>(resolve => canvas.toBlob(resolve, 'image/jpeg', 0.9))
    stopCamera(); await loadPhoto(blob ?? undefined)
  }
  async function enroll() {
    const person = people.find(item => item.id === personId && item.active)
    if (!person || !selectedDevices.length) {
      setError('Selecione a pessoa e os controladores autorizados.'); return
    }
    setBusy(true); setError(''); setMessage('')
    try {
      let last: FaceJob | null = null
      for (const selectedDevice of selectedDevices) {
        last = await postPortaria<FaceJob>(clientId, `people/${personId}/face-jobs`, { deviceId: selectedDevice, photoBase64: photo?.base64 ?? null })
      }
      setPhoto(null)
      setJobId(last?.id ?? '')
      setMessage(`Sincronização solicitada para ${person.name}. A foto fica salva no servidor da Portaria.`)
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Não foi possível enviar a face.')
    } finally { setBusy(false) }
  }

  async function pairAgent() {
    if (!deviceId) { setError('Selecione um controlador.'); return }
    setBusy(true); setError(''); setMessage('')
    try {
      const result = await postPortaria<{ agentKey: string }>(clientId, `facial-devices/${deviceId}/pair`)
      setAgentKey(result.agentKey)
      setMessage('Copie a chave para o agente no PC. Ela aparece somente agora e substitui a anterior.')
    } catch { setError('Não foi possível emitir a chave do agente.') }
    finally { setBusy(false) }
  }

  return <div className="portaria-card portaria-facial-pilot">
    <h3>Cadastro e sincronização facial</h3>
    <p>A foto fica salva de forma protegida na Portaria e pode ser reutilizada nos controladores autorizados. Sem selecionar uma nova foto, será usada a foto já salva.</p>
    <div className="portaria-form-grid">
      <label>Pessoa cadastrada<select value={personId} onChange={event => setPersonId(event.target.value)}>
        <option value="">Selecione</option>{people.filter(item => item.active).map(item => <option key={item.id} value={item.id}>{item.name}</option>)}
      </select></label>
      <label>Controlador facial<select value={deviceId} onChange={event => setDeviceId(event.target.value)}>
        <option value="">Selecione</option>{devices.map(item => <option key={item.id} value={item.id}>{item.name}{item.active ? '' : ' · inativo'}</option>)}
      </select></label>
    </div>
    <fieldset><legend>Controladores autorizados</legend>{devices.filter(item => item.active).map(item => <label className="portaria-check" key={item.id}><input type="checkbox" checked={selectedDevices.includes(item.id)} onChange={event => setSelectedDevices(current => event.target.checked ? [...current, item.id] : current.filter(id => id !== item.id))} />{item.name}</label>)}</fieldset>
    {statuses.map(item => <p key={item.deviceId}>{item.deviceName} · {item.jobStatus === 'failed' || item.jobStatus === 'expired' ? 'Erro' : item.status === 'active' && item.jobStatus !== 'queued' && item.jobStatus !== 'leased' ? 'Sincronizada' : 'Pendente'}</p>)}
    <div className="portaria-actions"><label className="portaria-photo-button">Escolher foto<input type="file" accept="image/*" onChange={event => void choosePhoto(event)} /></label>
      <button type="button" onClick={() => void openCamera()} disabled={cameraOpen}>Tirar foto agora</button></div>
    {cameraOpen && <div className="portaria-camera"><video ref={node => { video.current = node; if (node && stream.current) node.srcObject = stream.current }} autoPlay playsInline muted />
      <div className="portaria-actions"><button type="button" onClick={() => void capture()}>Usar esta foto</button><button type="button" className="portaria-quiet" onClick={stopCamera}>Cancelar</button></div></div>}
    {photo && <div className="portaria-photo-preview"><img src={photo.preview} alt="Prévia da foto que será enviada ao Intelbras" /><button type="button" className="portaria-quiet" onClick={() => setPhoto(null)}>Remover foto</button></div>}
    {error && <div className="portaria-error" role="alert">{error}</div>}
    {message && <div className="portaria-success" role="status">{message}</div>}
    {agentKey && <div className="portaria-success"><strong>Chave única do agente (copie agora):</strong> <code>{agentKey}</code><br />ID do controlador: <code>{deviceId}</code></div>}
    <div className="portaria-actions"><button type="button" disabled={busy || !selectedDevices.length || !personId} onClick={() => void enroll()}>{busy ? 'Enviando...' : 'Enviar / reenviar foto aos controladores'}</button>
      {canPairAgent && <button type="button" className="portaria-quiet" disabled={busy || !deviceId} onClick={() => void pairAgent()}>Emitir chave do agente</button>}</div>
  </div>
}
